// data-bridge.js — real data wiring against the same /api/ext/* endpoints
// the existing wx-app.js uses. We don't import from wx-app.js (it's an IIFE
// with no exports); we fetch independently. Browser + server-side caching
// (10 min for forecast, 60s for alerts) makes the duplication near-free.
//
// Public surface keeps the same names the variants imported in Phase 1, so
// porting the variants required no churn. Each getter accepts an optional
// `state` argument that, when set, returns the prototype's MOCK shape for
// that condition — used by the ?_dev_state=… URL param to cycle layouts
// for visual verification.

// ───────────────────── Constants & shared keys ─────────────────────────────
const CACHE_PREFIX = 'weatherfcc:v32826:';
const KEYS = {
  lastLocation: CACHE_PREFIX + 'last-location',
  favorites:    CACHE_PREFIX + 'favorites',
  favoritesMigrated: CACHE_PREFIX + 'favorites-migrated-v2',
  unit:         CACHE_PREFIX + 'temp-unit',
  // Shared with script.js — dismissing the geolocation offer on either
  // surface silences it on both.
  geoOfferDismissed: CACHE_PREFIX + 'geo-offer-dismissed',
};

const FALLBACK_LOCATION = { name: 'New York, NY', lat: 40.7128, lon: -74.0060 };

const IS_TENANT_WEATHER = (() => {
  try { return !!document.body?.dataset?.tenantSlug; } catch (_) { return false; }
})();

const LOCAL_ONLY_VIEWED_LOCATION = globalThis.document?.body?.dataset?.tenantSlug === "weatherdog";

const URLS = {
  forecast:       '/api/ext/open-meteo/forecast-full',
  current:        '/api/ext/open-meteo/current',
  alerts:         '/api/ext/nws/alerts',
  airQuality:     '/api/ext/open-meteo/air-quality',
  archive:        '/api/ext/open-meteo/archive',
  sun:            '/api/ext/sunrise-sunset',
  geocode:        IS_TENANT_WEATHER ? '/api/geo/suggest' : '/music/api/geo/suggest',
  reverseGeo:     '/api/ext/bigdatacloud/reverse-geocode',
  // Tenant domains use their own session + preference store behind /api/*.
  // fcc.cc keeps the central account endpoints under /music/api/*.
  authMe:         IS_TENANT_WEATHER ? '/api/auth/whoami' : '/music/api/auth/me',
  mePreferences:  IS_TENANT_WEATHER ? '/api/me/preferences' : '/music/api/me/preferences',
};

// Ported verbatim from wx-app.js WX_CODES so icons match desktop chrome.
const WX_CODES = {
  0:  { label: 'Clear',            icon: 'clear' },
  1:  { label: 'Mostly clear',     icon: 'clear' },
  2:  { label: 'Partly cloudy',    icon: 'partly-cloudy' },
  3:  { label: 'Overcast',         icon: 'cloudy' },
  45: { label: 'Fog',              icon: 'fog' },
  48: { label: 'Rime fog',         icon: 'fog' },
  51: { label: 'Light drizzle',    icon: 'drizzle' },
  53: { label: 'Drizzle',          icon: 'drizzle' },
  55: { label: 'Heavy drizzle',    icon: 'drizzle' },
  56: { label: 'Freezing drizzle', icon: 'drizzle' },
  57: { label: 'Freezing drizzle', icon: 'drizzle' },
  61: { label: 'Light rain',       icon: 'rain' },
  63: { label: 'Rain',             icon: 'rain' },
  65: { label: 'Heavy rain',       icon: 'rain' },
  66: { label: 'Freezing rain',    icon: 'rain' },
  67: { label: 'Freezing rain',    icon: 'rain' },
  71: { label: 'Light snow',       icon: 'snow' },
  73: { label: 'Snow',             icon: 'snow' },
  75: { label: 'Heavy snow',       icon: 'snow' },
  77: { label: 'Snow grains',      icon: 'snow' },
  80: { label: 'Rain showers',     icon: 'rain' },
  81: { label: 'Rain showers',     icon: 'rain' },
  82: { label: 'Heavy showers',    icon: 'rain' },
  85: { label: 'Snow showers',     icon: 'snow' },
  86: { label: 'Snow showers',     icon: 'snow' },
  95: { label: 'Thunderstorm',     icon: 'storm' },
  96: { label: 'Thunder + hail',   icon: 'storm' },
  99: { label: 'Thunder + hail',   icon: 'storm' },
};
const codeLookup = (c) => WX_CODES[c] || { label: 'Unknown', icon: 'unknown' };

// ───────────────────── Reactive state + subscribers ────────────────────────
const subscribers = new Set();
let STATE = {
  loading: true,
  error: null,
  location: null,    // { name, lat, lon }
  unit: readUnit(),  // restore the visitor's saved °F/°C choice on load
  forecast: null,    // raw Open-Meteo JSON (includes hourly, daily, minutely_15)
  forecastLocation: null, // coordinates the current forecast was fetched for
  alerts: [],        // NWS alert properties[]
  sun: null,         // { rise, set, dayLen, minsToSet }
  airQuality: null,  // raw Open-Meteo air-quality JSON (for pollen)
  historical: null,  // { records, avgs } from /api/ext/open-meteo/archive
  quote: null,       // { text, author, source } — today's rotated quote (lazy-loaded)
  auth: { loaded: false, authenticated: false, user: null },
  geoOffer: false,   // show the pre-permission location offer card (app.jsx)
};

function notify() { subscribers.forEach((fn) => { try { fn(STATE); } catch (_) {} }); }
function patch(p) { STATE = { ...STATE, ...p }; notify(); }

export function subscribe(fn) {
  subscribers.add(fn);
  Promise.resolve().then(() => fn(STATE));
  return () => subscribers.delete(fn);
}
export function getRawState() { return STATE; }

// ───────────────────── localStorage I/O ────────────────────────────────────
function readLastLocation() {
  try {
    const raw = localStorage.getItem(KEYS.lastLocation);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (p && p.lat != null && p.lon != null && p.name) {
      const lat = Number(p.lat);
      const lon = Number(p.lon);
      if (Number.isFinite(lat) && Number.isFinite(lon)) return { ...p, lat, lon };
    }
  } catch (_) {}
  return null;
}
function writeLastLocation(loc) {
  try { localStorage.setItem(KEYS.lastLocation, JSON.stringify(loc)); } catch (_) {}
}
function readUnit() {
  try { const u = localStorage.getItem(KEYS.unit); if (u === 'F' || u === 'C') return u; } catch (_) {}
  return 'F';
}
function readFavoritesRaw() {
  try {
    const raw = localStorage.getItem(KEYS.favorites);
    if (!raw) return [];
    const list = JSON.parse(raw);
    return Array.isArray(list) ? list : [];
  } catch (_) { return []; }
}

// ───────────────────── Fetches (mirror wx-app.js signatures) ───────────────
async function fetchForecastFull(lat, lon) {
  const u = new URL(URLS.forecast, globalThis.location.origin);
  u.searchParams.set('lat', String(lat));
  u.searchParams.set('lon', String(lon));
  u.searchParams.set('timezone', 'auto');
  u.searchParams.set('temperature_unit', 'celsius');
  u.searchParams.set('wind_speed_unit', 'mph');
  u.searchParams.set('forecast_days', '10');
  u.searchParams.set('current_weather', 'true');
  u.searchParams.set('hourly',
    'relativehumidity_2m,temperature_2m,apparent_temperature,dewpoint_2m,uv_index,' +
    'precipitation_probability,weathercode,is_day,wind_speed_10m,wind_direction_10m,wind_gusts_10m,pressure_msl');
  u.searchParams.set('daily',
    'weathercode,temperature_2m_max,temperature_2m_min,precipitation_probability_max,' +
    'precipitation_sum,sunrise,sunset,uv_index_max');
  // 15-min precip for the rain-next-hour card. Omitting this is why the
  // card never rendered on mobile — getMinutely() read a field the fetch
  // never asked for. The iOS app requests it and always had the card.
  u.searchParams.set('minutely_15', 'precipitation,precipitation_probability');
  const r = await fetch(u.toString(), { cache: 'no-store' });
  if (!r.ok) throw new Error('forecast HTTP ' + r.status);
  return r.json();
}

async function fetchNwsAlerts(lat, lon) {
  const u = URLS.alerts + '?lat=' + lat.toFixed(4) + '&lon=' + lon.toFixed(4);
  try {
    const r = await fetch(u, { headers: { Accept: 'application/geo+json' }, cache: 'no-store' });
    if (!r.ok) return [];
    const j = await r.json();
    return (j && Array.isArray(j.features) ? j.features : [])
      .map((f) => f && f.properties).filter(Boolean);
  } catch (_) { return []; }
}

const POLLEN_FIELDS = ['alder_pollen','birch_pollen','olive_pollen','grass_pollen','mugwort_pollen','ragweed_pollen'];

async function fetchAirQuality(lat, lon) {
  const u = new URL(URLS.airQuality, globalThis.location.origin);
  u.searchParams.set('lat', String(lat));
  u.searchParams.set('lon', String(lon));
  u.searchParams.set('hourly', POLLEN_FIELDS.join(','));
  u.searchParams.set('timezone', 'auto');
  u.searchParams.set('forecast_days', '1');
  try {
    const r = await fetch(u.toString(), { cache: 'no-store' });
    if (!r.ok) return null;
    return await r.json();
  } catch (_) { return null; }
}

async function fetchHistorical(lat, lon) {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day   = String(now.getDate()).padStart(2, '0');
  const year  = now.getFullYear();
  const startYear = year - 30;
  const u = new URL(URLS.archive, globalThis.location.origin);
  u.searchParams.set('lat', String(lat));
  u.searchParams.set('lon', String(lon));
  u.searchParams.set('start_date', `${startYear}-${month}-${day}`);
  u.searchParams.set('end_date',   `${year - 1}-${month}-${day}`);
  u.searchParams.set('daily', 'temperature_2m_max,temperature_2m_min,precipitation_sum');
  u.searchParams.set('temperature_unit', 'celsius');
  u.searchParams.set('timezone', 'auto');
  try {
    const r = await fetch(u.toString(), { cache: 'no-store' });
    if (!r.ok) return null;
    const data = await r.json();
    const times = (data.daily && data.daily.time) || [];
    const rows = [];
    times.forEach((d, i) => {
      if (!String(d).endsWith(`-${month}-${day}`)) return;
      const hi = data.daily.temperature_2m_max?.[i];
      const lo = data.daily.temperature_2m_min?.[i];
      rows.push({ year: Number(String(d).slice(0, 4)), high: isFinite(Number(hi)) ? Number(hi) : null, low: isFinite(Number(lo)) ? Number(lo) : null });
    });
    if (!rows.length) return null;
    const highs = rows.map((r) => r.high).filter((v) => isFinite(v));
    const lows  = rows.map((r) => r.low).filter((v) => isFinite(v));
    const recordHi = highs.length ? Math.max(...highs) : null;
    const recordLo = lows.length  ? Math.min(...lows)  : null;
    const avgHi = highs.length ? highs.reduce((a,b)=>a+b,0) / highs.length : null;
    const avgLo = lows.length  ? lows.reduce((a,b)=>a+b,0) / lows.length  : null;
    return {
      rows, recordHi, recordLo, avgHi, avgLo,
      recordHiYear: recordHi != null ? (rows.find((r) => r.high === recordHi)?.year ?? null) : null,
      recordLoYear: recordLo != null ? (rows.find((r) => r.low  === recordLo)?.year ?? null) : null,
    };
  } catch (_) { return null; }
}

// Monotonic token: account hydration can kick off a second loadAll while the
// initial localStorage-location load is still in flight — without this, the
// slower (stale) load would overwrite the newer location's data on arrival.
let loadSeq = 0;

async function loadAll(loc) {
  const seq = ++loadSeq;
  patch({ loading: true, error: null, location: loc });
  // Forecast drives first paint. Alerts are important, but the NWS relay can
  // be materially slower and must not hold the whole forecast behind it.
  fetchNwsAlerts(loc.lat, loc.lon).then((alerts) => {
    if (seq === loadSeq) patch({ alerts });
  }).catch(() => {});
  try {
    const forecast = await fetchForecastFull(loc.lat, loc.lon);
    if (seq !== loadSeq) return;
    const sun = deriveSun(forecast);
    patch({ loading: false, forecast, sun, forecastLocation: { lat: loc.lat, lon: loc.lon } });
  } catch (err) {
    if (seq !== loadSeq) return;
    patch({ loading: false, error: (err && err.message) || String(err) });
  }
  // Secondary fetches — fire-and-forget, sections light up when ready.
  fetchAirQuality(loc.lat, loc.lon).then((airQuality) => { if (seq === loadSeq) patch({ airQuality }); });
  fetchHistorical(loc.lat, loc.lon).then((historical) => { if (seq === loadSeq) patch({ historical }); });
}

// Public refresh — wired to PullToRefresh.
export function refresh() {
  const loc = STATE.location;
  if (loc) loadAll(loc);
}

// ───────────────────── Public mutators ─────────────────────────────────────
export function setLocation(loc) {
  if (!loc || loc.lat == null || loc.lon == null) return;
  const lat = Number(loc.lat);
  const lon = Number(loc.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
  const clean = { name: loc.name || `${lat.toFixed(2)}, ${lon.toFixed(2)}`, lat, lon };
  writeLastLocation(clean);
  loadAll(clean);
}

export function setUnit(u) {
  if (u !== 'F' && u !== 'C') return;
  try { localStorage.setItem(KEYS.unit, u); } catch (_) {}
  patch({ unit: u });
}

export async function geocodeSuggest(q) {
  if (!q || q.length < 2) return [];
  const u = new URL(URLS.geocode, globalThis.location.origin);
  u.searchParams.set('q', q);
  u.searchParams.set('limit', '6');
  try {
    const r = await fetch(u.toString(), { credentials: 'include', cache: 'no-store' });
    if (!r.ok) return [];
    const j = await r.json();
    return Array.isArray(j && j.results) ? j.results : [];
  } catch (_) { return []; }
}

export async function reverseGeocode(lat, lon) {
  const u = `${URLS.reverseGeo}?lat=${lat}&lon=${lon}`;
  try {
    const r = await fetch(u, { cache: 'force-cache' });
    if (!r.ok) return null;
    const j = await r.json();
    if (j && j.ok && j.label) return { label: j.label, countryCode: j.countryCode || '' };
  } catch (_) {}
  return null;
}

function improveLocationLabelInBackground(loc) {
  void reverseGeocode(loc.lat, loc.lon).then((hit) => {
    if (!hit || !hit.label) return;
    const current = STATE.location;
    if (!current
        || Math.abs(current.lat - loc.lat) > 0.00001
        || Math.abs(current.lon - loc.lon) > 0.00001) return;
    const named = { ...current, name: hit.label, countryCode: hit.countryCode || '' };
    writeLastLocation(named);
    patch({ location: named });
  }).catch(() => {});
}

// First-ever load (no stored location): the fallback (NY) is already
// rendering. NEVER fire the raw browser permission prompt unsolicited —
// early test visitors were bothered by the unexplained request. Instead:
//   granted → locate silently (no prompt of any kind appears)
//   denied / geolocation unavailable → raise the SEARCH variant of the offer
//             card (privacy browsers that block geo still get a nudge off
//             the fallback, pointed at search — no dead buttons)
//   prompt  → raise the GEO variant, which gates the native prompt behind
//             its "Use my location" button
// Cards are tenant-only (body[data-standalone]) — fcc.cc/weather never
// offers (its Permissions-Policy blocks geolocation sitewide anyway).
// Mirrors script.js maybeAutoLocateFirstLoad — keep the two in sync.
async function maybeAutoLocateFirstLoad() {
  const isTenant = (() => {
    try { return document.body?.dataset?.standalone === '1'; } catch (_) { return false; }
  })();
  const offerCard = (mode) => {
    if (!isTenant) return;
    try {
      const dismissed = localStorage.getItem(KEYS.geoOfferDismissed);
      if (dismissed === '1' || dismissed === 'true') return;
    } catch (_) {}
    patch({ geoOffer: mode });
  };
  if (!navigator.geolocation) { offerCard('search'); return; }
  let permState = 'prompt';
  try {
    permState = (await navigator.permissions.query({ name: 'geolocation' })).state;
  } catch (_) { /* Permissions API unavailable — the card still gates the prompt */ }
  if (permState === 'denied') { offerCard('search'); return; }
  const stillOnFallback = () => {
    if (readLastLocation()) return false; // user/account picked one meanwhile
    const cur = STATE.location;
    return !cur
      || (Math.abs(cur.lat - FALLBACK_LOCATION.lat) < 1e-9
        && Math.abs(cur.lon - FALLBACK_LOCATION.lon) < 1e-9);
  };
  if (permState === 'granted') {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (!stillOnFallback()) return;
        const lat = pos.coords.latitude;
        const lon = pos.coords.longitude;
        const loc = { name: `Near ${lat.toFixed(2)}, ${lon.toFixed(2)}`, lat, lon };
        writeLastLocation(loc);
        loadAll(loc);
        improveLocationLabelInBackground(loc);
      },
      () => { /* fix failed — fallback stays */ },
      { timeout: 12000, maximumAge: 300000 }
    );
    return;
  }
  if (!stillOnFallback()) return;
  offerCard('geo');
}

// Offer-card actions (rendered by app.jsx). hideGeoOffer just closes it for
// this view (accept path — success removes the need, failure shows an error
// inline first); dismissGeoOffer persists the shared never-again flag.
export function hideGeoOffer() { patch({ geoOffer: false }); }
export function dismissGeoOffer() {
  try { localStorage.setItem(KEYS.geoOfferDismissed, '1'); } catch (_) {}
  patch({ geoOffer: false });
}

export async function useGeolocation() {
  if (!navigator.geolocation) throw new Error('Geolocation unavailable');
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lon = pos.coords.longitude;
        // Paint from coordinates immediately. Reverse geocoding upgrades the
        // label later without delaying the forecast or the calling UI.
        const loc = { name: `Near ${lat.toFixed(2)}, ${lon.toFixed(2)}`, lat, lon };
        writeLastLocation(loc);
        loadAll(loc);
        improveLocationLabelInBackground(loc);
        resolve(loc);
      },
      (err) => reject(err),
      { timeout: 10000, maximumAge: 600000 }
    );
  });
}

// ───────────────────── Auth ────────────────────────────────────────────────
export function normalizeAuthPayload(payload) {
  const user = payload && typeof payload === 'object' ? (payload.user || null) : null;
  const authenticated = payload && typeof payload.authenticated === 'boolean'
    ? payload.authenticated
    : !!user;
  return { loaded: true, authenticated, user };
}

async function loadAuth() {
  try {
    const r = await fetch(URLS.authMe, { credentials: 'include', cache: 'no-store' });
    if (!r.ok) { patch({ auth: { loaded: true, authenticated: false, user: null } }); return; }
    const j = await r.json();
    patch({ auth: normalizeAuthPayload(j) });
  } catch (_) {
    patch({ auth: { loaded: true, authenticated: false, user: null } });
  }
}

// ───────────────────── Account hydration (read-only) ───────────────────────
// The desktop engines (script.js / wx-app.js) hold their whole startup
// pipeline while this overlay owns the screen, so script.js's
// hydrateFavoritesFromAccount no longer runs on phones — this bridge pulls
// the signed-in account's weather prefs itself and writes the same
// localStorage keys. Read-only by design: rows without coordinates are
// skipped (the desktop engine geocodes and resolves them), an empty account
// list leaves anonymous local favorites alone until the tenant claim prompt is
// answered; after migration, an empty server list intentionally clears the
// local cache. Nothing is written back to the account. Runs only after auth/me
// confirms a session, so anonymous visitors pay zero extra requests.
function prefsRowToLocation(row) {
  const name = String((row && (row.label || row.name)) || '').trim();
  const lat = Number(row && row.lat);
  const lon = Number(row && row.lon);
  if (!name || !isFinite(lat) || !isFinite(lon)) return null;
  return {
    name,
    lat: Number(lat.toFixed(4)),
    lon: Number(lon.toFixed(4)),
    countryCode: String((row && (row.countryCode || row.country_code)) || '').trim().slice(0, 8),
    accountFavoriteId: Number.isInteger(Number(row && row.favoriteId))
      ? Number(row.favoriteId)
      : null,
  };
}

let accountHydrationSeq = 0;
let lastResumeHydrationAt = 0;

async function hydrateFromAccount() {
  const seq = ++accountHydrationSeq;
  try {
    const r = await fetch(URLS.mePreferences, { credentials: 'include', cache: 'no-store' });
    if (!r.ok) return;
    const payload = await r.json();
    if (seq !== accountHydrationSeq) return;
    const prefs = payload && typeof payload.preferences === 'object' && payload.preferences
      ? payload.preferences : payload;
    if (!prefs || typeof prefs !== 'object') return;

    const favs = (Array.isArray(prefs.weather_locations) ? prefs.weather_locations : [])
      .map(prefsRowToLocation).filter(Boolean).slice(0, 8);
    let migrated = false;
    try { migrated = !!localStorage.getItem(KEYS.favoritesMigrated); } catch (_) {}
    // Preserve local rows until the first account claim has had a chance to
    // run. Thereafter the server is canonical, including an intentionally
    // empty list after the user removes their final favorite on another device.
    if (favs.length || migrated || readFavoritesRaw().length === 0) {
      try { localStorage.setItem(KEYS.favorites, JSON.stringify(favs)); } catch (_) {}
      try { localStorage.setItem(KEYS.favoritesMigrated, 'true'); } catch (_) {}
      notify(); // favorites strip re-reads localStorage on next render
    }

    // WeatherDog keeps viewed locations on-device, including when an older
    // server returns historical account state. Other weather sites retain
    // their existing account-location hydration behavior.
    const remoteLast = LOCAL_ONLY_VIEWED_LOCATION ? null : prefsRowToLocation(prefs.weather_last_location);
    if (remoteLast) {
      if (seq !== accountHydrationSeq) return;
      writeLastLocation(remoteLast);
      const cur = STATE.location;
      const moved = !cur
        || Math.abs(cur.lat - remoteLast.lat) > 0.005
        || Math.abs(cur.lon - remoteLast.lon) > 0.005;
      if (moved) loadAll(remoteLast);
    }
  } catch (_) { /* best effort — account sync must never break the app */ }
}

function hydrateAccountOnResume() {
  if (!STATE.auth.authenticated) return;
  const now = Date.now();
  if (now - lastResumeHydrationAt < 1000) return;
  lastResumeHydrationAt = now;
  void hydrateFromAccount();
}

export function getAuth() { return STATE.auth || { loaded: false, authenticated: false, user: null }; }
export function authReturnUrl() {
  // /account/ is the sign-in entry. Pass a `next` param so we return here.
  const next = encodeURIComponent(globalThis.location?.pathname || '/weather/');
  return `/account/?next=${next}`;
}

// ───────────────────── Format helpers ──────────────────────────────────────
const cToF = (c) => Math.round(c * 9 / 5 + 32);
const fmtTemp = (c, unit) => {
  if (c == null || !isFinite(c)) return null;
  return unit === 'C' ? Math.round(c) : cToF(c);
};

// "YYYY-MM-DDTHH" for now, on the forecast location's wall clock. Open-Meteo
// returns hourly.time as "2026-05-25T14:00" in the location's tz when
// timezone=auto, so "now" must be read in that zone — not the browser's hour
// and not the UTC date. Pairing the UTC date with the browser hour picked
// tomorrow's same hour every evening once UTC passed midnight (fcc.cc/weather
// showed "68°F · Feels like 32°" in New York at 8 pm on 2026-10-16).
function locationHourKey(forecast, nowMs) {
  const tz = String(forecast.timezone || '').trim();
  if (tz) {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23',
      }).formatToParts(new Date(nowMs));
      const v = Object.fromEntries(parts.map((p) => [p.type, p.value]));
      if (v.year && v.month && v.day && v.hour) {
        return `${v.year}-${v.month}-${v.day}T${v.hour === '24' ? '00' : v.hour}`;
      }
    } catch (_) {}
  }
  const offset = Number(forecast.utc_offset_seconds);
  if (Number.isFinite(offset)) return new Date(nowMs + offset * 1000).toISOString().slice(0, 13);
  return String((forecast.current_weather && forecast.current_weather.time) || '').slice(0, 13);
}

function hourlyIndexForNow(forecast) {
  const times = forecast && forecast.hourly && forecast.hourly.time;
  if (!Array.isArray(times) || !times.length) return -1;
  const key = locationHourKey(forecast, Date.now());
  if (!key) return 0;
  // Latest hour at or before now — the exact hour on any normal load.
  let idx = 0;
  for (let i = 0; i < times.length; i++) {
    if (String(times[i]).slice(0, 13) <= key) idx = i;
    else break;
  }
  return idx;
}

function deriveSun(forecast) {
  if (!forecast || !forecast.daily) return null;
  const rise = forecast.daily.sunrise && forecast.daily.sunrise[0];
  const set  = forecast.daily.sunset  && forecast.daily.sunset[0];
  if (!rise || !set) return null;
  const riseD = new Date(rise);
  const setD  = new Date(set);
  const fmt = (d) => d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).toLowerCase().replace(/\s/g, ' ');
  const dayMin = Math.round((setD - riseD) / 60000);
  const dayLen = `${Math.floor(dayMin / 60)}h ${dayMin % 60}m`;
  const minsToSet = Math.max(0, Math.round((setD - new Date()) / 60000));
  return { rise: fmt(riseD), set: fmt(setD), dayLen, minsToSet, riseIso: rise, setIso: set };
}

// ───────────────────── Mock fallback (Phase 1 shape) ───────────────────────
// Used when (a) the dev URL ?_dev_state=… is set, or (b) real data hasn't
// arrived yet so the layout has something to render.
const MOCK = {
  states: {
    clear:  { temp: 67, feels: 65, cond: 'Clear and sunny', icon: 'clear',         hi: 71, lo: 52, pop: 0  },
    cloudy: { temp: 54, feels: 52, cond: 'Mostly cloudy',   icon: 'partly-cloudy', hi: 58, lo: 47, pop: 10 },
    rain:   { temp: 47, feels: 43, cond: 'Light rain',      icon: 'rain',          hi: 51, lo: 41, pop: 78 },
    storm:  { temp: 64, feels: 64, cond: 'Thunderstorms',   icon: 'storm',         hi: 76, lo: 60, pop: 92 },
    snow:   { temp: 28, feels: 19, cond: 'Snow showers',    icon: 'snow',          hi: 32, lo: 21, pop: 88 },
    fog:    { temp: 51, feels: 50, cond: 'Dense fog',       icon: 'fog',           hi: 56, lo: 48, pop: 5  },
  },
  alert: {
    severity: 'severe',
    head: 'Flood Watch',
    body: 'In effect through 8:00 PM EST. Heavy rainfall may produce localized flooding. — National Weather Service',
  },
  fieldNotes: [
    'Today is shorter than the equinox by 4 hours and 23 minutes.',
    'Pressure has fallen 0.18 in over 6 hours — front approaching.',
    'Last year on this date: 51°F and partly cloudy.',
  ],
  quote: {
    text: 'It was one of those March days when the sun shines hot and the wind blows cold: when it is summer in the light, and winter in the shade.',
    author: 'Charles Dickens', source: 'Great Expectations',
  },
};

const stateFromIcon = (icon) => {
  if (icon === 'partly-cloudy') return 'cloudy';
  if (icon === 'drizzle')       return 'rain';
  if (icon === 'clear-night')   return 'clear';
  if (['clear','cloudy','rain','storm','snow','fog'].includes(icon)) return icon;
  return 'cloudy';
};

// ───────────────────── Public getters (sync — read STATE) ──────────────────
export function getLocation() {
  const loc = STATE.location || FALLBACK_LOCATION;
  return { label: loc.name, region: '', lat: loc.lat, lon: loc.lon };
}

export function getUpdatedLabel() {
  if (STATE.loading) return 'Loading…';
  if (STATE.error)   return 'Couldn’t refresh';
  if (!STATE.forecast) return '';
  // Open-Meteo doesn't return a generated-at; use current hour wall-clock.
  return 'Updated just now';
}

export function getSun() {
  if (STATE.sun) return STATE.sun;
  return { rise: '—', set: '—', dayLen: '—', minsToSet: 0 };
}

export function getActiveStateString(devOverride) {
  if (devOverride && MOCK.states[devOverride]) return devOverride;
  const cur = getCurrentRealOrNull();
  return cur ? stateFromIcon(cur.icon) : 'cloudy';
}

function getCurrentRealOrNull() {
  const f = STATE.forecast;
  if (!f || !f.current_weather) return null;
  const c = codeLookup(f.current_weather.weathercode);
  const idx = hourlyIndexForNow(f);
  const hourly = f.hourly || {};
  const daily = f.daily || {};
  // Open-Meteo's current_weather returns is_day (0|1). Swap the clear / partly-cloudy
  // icons to their night variants when the sun is down so the hero readout matches
  // what the visitor actually sees outside.
  const isDay = f.current_weather.is_day !== 0;
  let icon = c.icon;
  let cond = c.label;
  if (!isDay) {
    if (icon === 'clear')         { icon = 'clear-night'; cond = 'Clear'; }
    if (icon === 'partly-cloudy') { icon = 'partly-cloudy'; /* prototype has no night variant; keep */ }
  }
  return {
    temp:  fmtTemp(f.current_weather.temperature,                              STATE.unit),
    feels: fmtTemp(hourly.apparent_temperature && hourly.apparent_temperature[idx], STATE.unit),
    cond,
    icon,
    isDay,
    hi:    fmtTemp(daily.temperature_2m_max && daily.temperature_2m_max[0],    STATE.unit),
    lo:    fmtTemp(daily.temperature_2m_min && daily.temperature_2m_min[0],    STATE.unit),
    pop:   Math.round((daily.precipitation_probability_max && daily.precipitation_probability_max[0]) || 0),
  };
}

// PHASE 2.5 BUG FIX: getActiveStateString returns a derived name like 'clear'
// from real forecast, but old code path treated any state-name arg as a dev
// override → returned MOCK.states.clear instead of real values. New rule:
// the dev override is read INSIDE the getter from devStateOverride(); the
// component-supplied state arg is ignored for live data.
export function getCurrentForState(_componentState) {
  const dev = devStateOverride();
  if (dev && MOCK.states[dev]) return MOCK.states[dev];
  return getCurrentRealOrNull() || MOCK.states.cloudy;
}

export function getMetrics(_componentState) {
  const dev = devStateOverride();
  if (dev && MOCK.states[dev]) {
    return metricsFromCurrent(MOCK.states[dev], null, null);
  }
  const f = STATE.forecast;
  const cur = getCurrentRealOrNull();
  if (!cur || !f) return metricsFromCurrent(MOCK.states.cloudy, null, null);
  const idx = hourlyIndexForNow(f);
  const hourly = f.hourly || {};
  return metricsFromCurrent(cur,
    {
      humidity: hourly.relativehumidity_2m && hourly.relativehumidity_2m[idx],
      dew:      hourly.dewpoint_2m && hourly.dewpoint_2m[idx],
      windMph:  hourly.wind_speed_10m && hourly.wind_speed_10m[idx],
      windDeg:  hourly.wind_direction_10m && hourly.wind_direction_10m[idx],
      uv:       hourly.uv_index && hourly.uv_index[idx],
      pressure: hourly.pressure_msl && hourly.pressure_msl[idx],
    }, STATE.unit);
}

function metricsFromCurrent(cur, live, unit) {
  // Live data path (real forecast); otherwise mock-derived placeholders.
  const cdir = (deg) => {
    if (deg == null || !isFinite(deg)) return '';
    const dirs = ['N','NE','E','SE','S','SW','W','NW'];
    return dirs[Math.round(deg / 45) % 8];
  };
  const uvLabel = (uv) => {
    const u = Math.round(uv || 0);
    return ['low','low','low','mod','mod','high','high','very high','very high','extreme','extreme','extreme'][u] || 'mod';
  };
  if (live) {
    const dewF = live.dew == null ? null : (unit === 'C' ? Math.round(live.dew) : cToF(live.dew));
    const pressureIn = live.pressure ? (live.pressure * 0.02953).toFixed(2) : null;
    return [
      { k: 'Feels like', v: (cur.feels != null ? cur.feels + '°' : '—'),
        sub: (cur.feels != null && cur.temp != null) ? (cur.feels < cur.temp ? `${cur.temp - cur.feels}° cooler` : cur.feels > cur.temp ? `${cur.feels - cur.temp}° warmer` : 'on the nose') : '',
        why: 'Apparent temperature combines air temp, humidity, and wind. Below 50°F, wind chill dominates.' },
      { k: 'Humidity', v: (live.humidity != null ? Math.round(live.humidity) + '%' : '—'),
        sub: dewF != null ? `dew pt ${dewF}°` : '',
        why: 'Relative humidity. Dew point is what matters for comfort — above 65°F it feels muggy.' },
      { k: 'Wind', v: (live.windMph != null ? Math.round(live.windMph) + ' mph' : '—'),
        sub: cdir(live.windDeg),
        why: 'Sustained wind. Gusts can be 1.4× higher in convective weather.' },
      { k: 'UV index', v: (live.uv != null ? Math.round(live.uv) : '—'),
        sub: live.uv != null ? uvLabel(live.uv) : '',
        why: 'UV index over a clear sky. Sunscreen recommended above 3.' },
      { k: 'Pressure', v: (pressureIn || '—'),
        sub: pressureIn ? 'in' : '',
        why: 'Sea-level pressure. Rising = improving weather; falling = storms approaching.' },
      { k: 'Visibility', v: '10 mi', sub: 'good',
        why: 'How far you can see horizontally. Below 1 mile is considered low.' },
    ];
  }
  // Mock fallback — same shape so layouts don't break before data lands.
  const k = cur.icon === 'partly-cloudy' ? 'cloudy' : cur.icon;
  const humidByState = { clear:38, cloudy:64, rain:88, storm:81, snow:74, fog:96 };
  const windByState  = { clear:6,  cloudy:11, rain:14, storm:22, snow:18, fog:3  };
  const uvByState    = { clear:7,  cloudy:3,  rain:1,  storm:2,  snow:2,  fog:1  };
  const visByState   = { fog:0.4,  rain:6,    snow:1.5,storm:3 };
  const uvN = uvByState[k] || 3;
  return [
    { k: 'Feels like', v: cur.feels + '°', sub: cur.feels < cur.temp ? `${cur.temp - cur.feels}° cooler` : 'on the nose',
      why: 'Apparent temperature combines air temp, humidity, and wind. Below 50°F, wind chill dominates.' },
    { k: 'Humidity', v: (humidByState[k] || 60) + '%', sub: 'dew pt ' + (cur.temp - 12) + '°',
      why: 'Relative humidity. Dew point is what matters for comfort — above 65°F it feels muggy.' },
    { k: 'Wind', v: (windByState[k] || 8) + ' mph', sub: 'NW',
      why: 'Sustained wind. Gusts can be 1.4× higher in convective weather.' },
    { k: 'UV index', v: uvN, sub: uvLabel(uvN),
      why: 'UV index over a clear sky. Sunscreen recommended above 3.' },
    { k: 'Pressure', v: '30.12', sub: 'in ↑ 0.04',
      why: 'Sea-level pressure. Rising = improving weather; falling = storms approaching.' },
    { k: 'Visibility', v: (visByState[cur.icon] || 10) + ' mi', sub: cur.icon === 'fog' ? 'dense' : 'good',
      why: 'How far you can see horizontally. Below 1 mile is considered low.' },
  ];
}

export function getHourly(_componentState) {
  const dev = devStateOverride();
  if (dev && MOCK.states[dev]) return mockHourly(MOCK.states[dev]);
  const f = STATE.forecast;
  if (!f || !f.hourly || !f.hourly.time) return mockHourly(MOCK.states.cloudy);
  const idx = hourlyIndexForNow(f);
  const hours = [];
  for (let i = 0; i < 12 && (idx + i) < f.hourly.time.length; i++) {
    const j = idx + i;
    const code = f.hourly.weathercode && f.hourly.weathercode[j];
    const isDay = f.hourly.is_day ? !!f.hourly.is_day[j] : true;
    const tC = f.hourly.temperature_2m && f.hourly.temperature_2m[j];
    const pop = (f.hourly.precipitation_probability && f.hourly.precipitation_probability[j]) || 0;
    let icon = codeLookup(code).icon;
    if (!isDay && icon === 'clear') icon = 'clear-night';
    // Partly-cloudy at night renders weirdly (sun behind clouds) — fall back
    // to 'cloudy' so the hourly cell shows just clouds against the night.
    if (!isDay && icon === 'partly-cloudy') icon = 'cloudy';
    const t = new Date(String(f.hourly.time[j]).replace(' ', 'T'));
    const label = i === 0 ? 'Now' :
      t.toLocaleTimeString([], { hour: 'numeric' }).toLowerCase().replace(/\s/g, '');
    hours.push({ time: label, temp: fmtTemp(tC, STATE.unit), icon, pop: Math.round(pop) });
  }
  while (hours.length < 12) hours.push({ time: '—', temp: 0, icon: 'unknown', pop: 0 });
  return hours;
}

function mockHourly(s) {
  const base = s.temp;
  const curve = [0, +1, +2, +2, +1, -1, -3, -5, -6, -7, -8, -9];
  const popCurve = {
    clear:   [0,0,0,0,5,5,0,0,0,0,0,0],
    cloudy:  [10,10,15,20,20,15,10,5,5,5,0,0],
    rain:    [78,82,85,80,70,55,40,30,25,20,15,10],
    storm:   [92,95,90,75,55,40,30,20,10,5,5,5],
    snow:    [88,90,85,75,65,55,45,35,25,15,10,10],
    fog:     [5,5,10,10,15,20,30,35,40,40,35,30],
  };
  const k = s.icon === 'partly-cloudy' ? 'cloudy' : s.icon;
  const pops = popCurve[k] || popCurve.cloudy;
  const icons = s.icon === 'partly-cloudy'
    ? ['partly-cloudy','partly-cloudy','partly-cloudy','cloudy','cloudy','cloudy','clear-night','clear-night','clear-night','clear-night','clear-night','clear-night']
    : s.icon === 'clear'
      ? ['clear','clear','clear','clear','clear','clear','clear-night','clear-night','clear-night','clear-night','clear-night','clear-night']
      : Array(12).fill(s.icon);
  return ['Now','3pm','4pm','5pm','6pm','7pm','8pm','9pm','10pm','11pm','12am','1am'].map((time, i) => ({
    time, temp: base + curve[i], icon: icons[i], pop: pops[i],
  }));
}

export function getTenDay(_componentState) {
  const dev = devStateOverride();
  if (dev && MOCK.states[dev]) return mockTenDay(MOCK.states[dev]);
  const f = STATE.forecast;
  if (!f || !f.daily || !f.daily.time) return mockTenDay(MOCK.states.cloudy);
  const dayLabels = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const out = [];
  for (let i = 0; i < Math.min(10, f.daily.time.length); i++) {
    const d = new Date(String(f.daily.time[i]).replace(' ', 'T') + (String(f.daily.time[i]).includes('T') ? '' : 'T12:00'));
    const code = f.daily.weathercode && f.daily.weathercode[i];
    const c = codeLookup(code);
    const hi = fmtTemp(f.daily.temperature_2m_max && f.daily.temperature_2m_max[i], STATE.unit);
    const lo = fmtTemp(f.daily.temperature_2m_min && f.daily.temperature_2m_min[i], STATE.unit);
    const pop = Math.round((f.daily.precipitation_probability_max && f.daily.precipitation_probability_max[i]) || 0);
    out.push({
      day: i === 0 ? 'Today' : dayLabels[d.getDay()],
      hi: hi != null ? hi : 0,
      lo: lo != null ? lo : 0,
      icon: c.icon,
      cond: c.label,
      pop,
    });
  }
  while (out.length < 10) out.push({ day: '—', hi: 0, lo: 0, icon: 'unknown', cond: '', pop: 0 });
  return out;
}

function mockTenDay(s) {
  const today = { day: 'Today', hi: s.hi, lo: s.lo, icon: s.icon, cond: s.cond, pop: s.pop };
  return [today,
    { day: 'Tue', hi: 64, lo: 49, icon: 'partly-cloudy', cond: 'Partly cloudy',    pop: 10 },
    { day: 'Wed', hi: 58, lo: 47, icon: 'rain',          cond: 'Showers, breezy',  pop: 72 },
    { day: 'Thu', hi: 55, lo: 44, icon: 'rain',          cond: 'Rain easing',      pop: 55 },
    { day: 'Fri', hi: 61, lo: 46, icon: 'cloudy',        cond: 'Overcast',         pop: 20 },
    { day: 'Sat', hi: 68, lo: 51, icon: 'partly-cloudy', cond: 'Sun & clouds',     pop: 5  },
    { day: 'Sun', hi: 73, lo: 55, icon: 'clear',         cond: 'Sunny, mild',      pop: 0  },
    { day: 'Mon', hi: 75, lo: 58, icon: 'clear',         cond: 'Sunny',            pop: 0  },
    { day: 'Tue', hi: 72, lo: 60, icon: 'storm',         cond: 'PM thunderstorms', pop: 78 },
    { day: 'Wed', hi: 67, lo: 54, icon: 'partly-cloudy', cond: 'Clearing',         pop: 15 },
  ];
}

export function getFavorites() {
  const list = readFavoritesRaw();
  if (!list.length) {
    // Show the active location at minimum so the strip isn't empty
    const loc = STATE.location || FALLBACK_LOCATION;
    return [{ id: 'active', label: loc.name.split(',')[0] || loc.name, region: loc.name.includes(',') ? loc.name.split(',').slice(1).join(',').trim() : '', temp: 0, icon: 'unknown', cond: '—' }];
  }
  return list.slice(0, 8).map((f, i) => ({
    id: `fav-${i}`,
    label: (f.name || 'Saved').split(',')[0],
    region: (f.name || '').includes(',') ? (f.name || '').split(',').slice(1).join(',').trim() : '',
    temp: 0, icon: 'unknown', cond: '',
    lat: f.lat, lon: f.lon, name: f.name,
  }));
}

export function getAlert() {
  if (STATE.alerts && STATE.alerts.length) {
    const a = STATE.alerts[0];
    const sev = (a.severity || 'moderate').toLowerCase();
    return {
      severity: ['extreme','severe'].includes(sev) ? 'severe' : sev === 'minor' ? 'minor' : 'moderate',
      head: a.event || a.headline || 'Weather alert',
      body: (a.headline && a.headline !== a.event ? a.headline + ' — ' : '') + (a.description || ''),
    };
  }
  return MOCK.alert;
}

export function hasActiveAlert() {
  return Array.isArray(STATE.alerts) && STATE.alerts.length > 0;
}

// Quote of the day — fetch the real rotating quote (was a frozen MOCK). Mirrors
// the desktop extras.js daily rotation (same hash → same quote per local day) so
// mobile + desktop agree. Lazy-loaded on first getQuote(); MOCK is the fallback
// until the fetch resolves (then patch→notify re-renders with the real quote).
function quoteHash(s) { let h = 0; for (let i = 0; i < s.length; i++) { h = ((h << 5) - h + s.charCodeAt(i)) | 0; } return Math.abs(h); }
function quoteTodayKey() { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }
let QUOTE_LOADED = false;
function loadQuoteOfDay() {
  if (QUOTE_LOADED) return;
  QUOTE_LOADED = true;
  fetch('./data/weather-quotes.json', { cache: 'force-cache' })
    .then((r) => (r.ok ? r.json() : null))
    .then((quotes) => {
      if (!Array.isArray(quotes) || !quotes.length) return;
      const q = quotes[quoteHash(quoteTodayKey()) % quotes.length];
      if (q && typeof q.text === 'string') {
        patch({ quote: { text: q.text, author: q.author || 'Unknown', source: q.source + (q.year ? ` (${q.year})` : '') } });
      }
    })
    .catch(() => {});
}
export function getQuote() { loadQuoteOfDay(); return STATE.quote || MOCK.quote; }

// Almanac — real historical when loaded; mock as placeholder. Numbers come in °C
// from the archive endpoint and get unit-converted here.
export function getAlmanac() {
  const h = STATE.historical;
  if (!h) return { avgHi: 52, avgLo: 38, recordHi: 78, recordHiYear: 1989, recordLo: 18, recordLoYear: 1972, loaded: false };
  const u = STATE.unit;
  return {
    avgHi: h.avgHi != null ? fmtTemp(h.avgHi, u) : null,
    avgLo: h.avgLo != null ? fmtTemp(h.avgLo, u) : null,
    recordHi: h.recordHi != null ? fmtTemp(h.recordHi, u) : null,
    recordLo: h.recordLo != null ? fmtTemp(h.recordLo, u) : null,
    recordHiYear: h.recordHiYear,
    recordLoYear: h.recordLoYear,
    loaded: true,
  };
}

// Historical comparison — "today vs 30-yr average". Compares forecast.daily[0]
// highs/lows against the archive average. Returns null until both loaded.
export function getHistoricalDelta() {
  const h = STATE.historical;
  const f = STATE.forecast;
  if (!h || !f || !f.daily) return null;
  const todayHi = f.daily.temperature_2m_max?.[0];
  const todayLo = f.daily.temperature_2m_min?.[0];
  if (todayHi == null || h.avgHi == null) return null;
  const deltaHi = Math.round(todayHi - h.avgHi);
  const deltaLo = todayLo != null && h.avgLo != null ? Math.round(todayLo - h.avgLo) : null;
  return { deltaHi, deltaLo, todayHi: fmtTemp(todayHi, STATE.unit), avgHi: fmtTemp(h.avgHi, STATE.unit) };
}

// Field notes — computed from real data, with placeholders before data arrives.
export function getFieldNotes() {
  const out = [];
  const sun = STATE.sun;
  if (sun && sun.dayLen && sun.dayLen !== '—') {
    out.push(`Today's day length is ${sun.dayLen}.`);
  }
  const hist = getHistoricalDelta();
  if (hist) {
    const sign = hist.deltaHi > 0 ? `${hist.deltaHi}° warmer` : hist.deltaHi < 0 ? `${Math.abs(hist.deltaHi)}° cooler` : 'right on the 30-year average';
    out.push(`Today's high is ${sign} than the 30-year average for this date.`);
  }
  const m = getMoon();
  if (m) out.push(`Moon is ${m.phase.toLowerCase()} (${m.illum}% illuminated).`);
  return out.length ? out : MOCK.fieldNotes;
}

// Sun countdown — minutes to sunset/sunrise; phrased for the hero panel.
export function getSunCountdown() {
  const sun = STATE.sun;
  if (!sun) return null;
  const m = sun.minsToSet;
  if (m > 0 && m < 60 * 12) {
    const h = Math.floor(m / 60);
    return { label: h > 0 ? `Sunset in ${h}h ${m % 60}m` : `Sunset in ${m}m`, sub: `${sun.set} · day length ${sun.dayLen}` };
  }
  // After sunset: countdown to sunrise (rough — uses next-day sunrise if loaded)
  const f = STATE.forecast;
  if (f && f.daily && f.daily.sunrise && f.daily.sunrise[1]) {
    const next = new Date(f.daily.sunrise[1]);
    const mins = Math.max(0, Math.round((next - new Date()) / 60000));
    const h = Math.floor(mins / 60);
    return { label: `Sunrise in ${h}h ${mins % 60}m`, sub: `Tomorrow ${next.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).toLowerCase()}` };
  }
  return { label: 'After sunset', sub: `Sunset was ${sun.set}` };
}

// Wind detail — current wind speed/direction/gusts from the now-hour slot.
export function getWindDetail() {
  const f = STATE.forecast;
  if (!f || !f.hourly) return null;
  const idx = hourlyIndexForNow(f);
  const speed = f.hourly.wind_speed_10m?.[idx];
  const deg   = f.hourly.wind_direction_10m?.[idx];
  const gusts = f.hourly.wind_gusts_10m?.[idx];
  if (speed == null) return null;
  const dirs = ['N','NE','E','SE','S','SW','W','NW'];
  return {
    speed: Math.round(speed),
    deg: Math.round(deg || 0),
    dir: dirs[Math.round((deg || 0) / 45) % 8],
    gusts: gusts != null ? Math.round(gusts) : null,
  };
}

// UV index timeline — next 12 daylight hours.
export function getUvTimeline() {
  const f = STATE.forecast;
  if (!f || !f.hourly || !f.hourly.uv_index) return null;
  const idx = hourlyIndexForNow(f);
  const out = [];
  for (let i = 0; i < 12; i++) {
    const j = idx + i;
    if (j >= f.hourly.uv_index.length) break;
    const t = new Date(String(f.hourly.time[j]).replace(' ', 'T'));
    out.push({
      hour: i === 0 ? 'Now' : t.toLocaleTimeString([], { hour: 'numeric' }).toLowerCase().replace(/\s/g, ''),
      uv: Math.round(f.hourly.uv_index[j] || 0),
    });
  }
  const max = Math.max(0, ...out.map((p) => p.uv));
  return { points: out, max };
}

// Minutely precipitation — next 90 min in 15-min slices.
export function getMinutely() {
  const f = STATE.forecast;
  const m = f && f.minutely_15;
  if (!m || !m.time || !m.precipitation) return null;
  const now = Date.now();
  const slices = [];
  for (let i = 0; i < m.time.length && slices.length < 6; i++) {
    const t = Date.parse(String(m.time[i]).replace(' ', 'T'));
    if (!isFinite(t) || t < now - 5 * 60_000) continue;
    slices.push({
      time: new Date(t).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).toLowerCase().replace(/\s/g, ' '),
      precip: m.precipitation[i] || 0,
      pop: m.precipitation_probability?.[i] || 0,
    });
  }
  if (!slices.length) return null;
  const maxP = Math.max(0.01, ...slices.map((s) => s.precip));
  const totalP = slices.reduce((a, b) => a + b.precip, 0);
  return { slices, maxP, totalP };
}

// Pollen — Europe-only via CAMS. Returns sorted highest-first; or null if no data.
const POLLEN_LABELS = { alder_pollen: 'Alder', birch_pollen: 'Birch', olive_pollen: 'Olive', grass_pollen: 'Grass', mugwort_pollen: 'Mugwort', ragweed_pollen: 'Ragweed' };
export function getPollen() {
  const aq = STATE.airQuality;
  if (!aq || !aq.hourly) return null;
  const out = [];
  for (const f of POLLEN_FIELDS) {
    const series = aq.hourly[f];
    if (!Array.isArray(series)) continue;
    const max = Math.max(0, ...series.slice(0, 24).map((v) => Number(v) || 0));
    if (max <= 0) continue;
    const tier = max > 100 ? 'High' : max > 30 ? 'Moderate' : 'Low';
    out.push({ kind: POLLEN_LABELS[f], pct: Math.min(100, Math.round(max)), tier });
  }
  if (!out.length) return null;
  return out.sort((a, b) => b.pct - a.pct).slice(0, 4);
}

// Moon phase — pure client-side synodic-month calculation. No fetch needed.
// Reference: 2000-01-06 18:14 UTC was a known new moon. Synodic month = 29.530588 days.
export function getMoon(daysAhead = 0) {
  const SYNODIC = 29.530588;
  const REF = Date.UTC(2000, 0, 6, 18, 14);
  const now = Date.now() + daysAhead * 86400_000;
  const days = ((now - REF) / 86400_000) % SYNODIC;
  const age = days < 0 ? days + SYNODIC : days;
  const frac = age / SYNODIC;
  const illum = Math.round(50 * (1 - Math.cos(2 * Math.PI * frac)));
  let phase, emoji;
  if (frac < 0.03 || frac > 0.97) { phase = 'New moon';        emoji = '🌑'; }
  else if (frac < 0.22)           { phase = 'Waxing crescent'; emoji = '🌒'; }
  else if (frac < 0.28)           { phase = 'First quarter';   emoji = '🌓'; }
  else if (frac < 0.47)           { phase = 'Waxing gibbous';  emoji = '🌔'; }
  else if (frac < 0.53)           { phase = 'Full moon';       emoji = '🌕'; }
  else if (frac < 0.72)           { phase = 'Waning gibbous';  emoji = '🌖'; }
  else if (frac < 0.78)           { phase = 'Last quarter';    emoji = '🌗'; }
  else                            { phase = 'Waning crescent'; emoji = '🌘'; }
  return { phase, illum, emoji, age };
}

export function getMoonRow(nights = 5) {
  const days = ['Today','Tue','Wed','Thu','Fri','Sat','Sun','Mon'];
  const out = [];
  const startDay = new Date().getDay();
  for (let i = 0; i < nights; i++) {
    const m = getMoon(i);
    const label = i === 0 ? 'Tonight' : days[(startDay + i) % 7];
    out.push({ day: label, phase: m.phase, illum: m.illum, emoji: m.emoji });
  }
  return out;
}

// Suggestions — what to wear / do based on current conditions + UV + wind + precip.
export function getSuggestions() {
  const cur = getCurrentRealOrNull();
  const wind = getWindDetail();
  const uv = STATE.forecast?.daily?.uv_index_max?.[0];
  const pop = cur?.pop ?? 0;
  const tempF = STATE.unit === 'C' ? (cur ? Math.round(cur.temp * 9/5 + 32) : 60) : (cur?.temp ?? 60);
  const out = [];

  if (pop >= 60) out.push({ icon: '☂️', text: 'Take an umbrella — high chance of precip.' });
  else if (pop >= 30) out.push({ icon: '🌂', text: 'Light umbrella or hood — passing showers possible.' });

  if (tempF >= 80) out.push({ icon: '🥵', text: 'Light layers + sunscreen. Stay hydrated.' });
  else if (tempF >= 65) out.push({ icon: '👕', text: 'T-shirt weather — comfortable mid-layer for evening.' });
  else if (tempF >= 50) out.push({ icon: '🧥', text: 'Light jacket. Long sleeves recommended.' });
  else if (tempF >= 32) out.push({ icon: '🧣', text: 'Warm coat, hat, gloves for extended time outside.' });
  else                  out.push({ icon: '❄️', text: 'Bundle up — heavy coat, scarf, hat, mittens.' });

  if (uv != null && uv >= 6) out.push({ icon: '🕶', text: `High UV (${Math.round(uv)}). Sunglasses + SPF 30+.` });

  if (wind && wind.gusts && wind.gusts >= 25) out.push({ icon: '💨', text: `Gusty (${wind.gusts} mph gusts from the ${wind.dir}). Secure loose items.` });
  else if (wind && wind.speed >= 15) out.push({ icon: '🍃', text: `Breezy (${wind.speed} mph from the ${wind.dir}).` });

  if (cur?.icon === 'fog') out.push({ icon: '🚗', text: 'Dense fog — extra time and headlights for driving.' });
  if (cur?.icon === 'snow') out.push({ icon: '🥾', text: 'Boots + grippy soles. Drive slowly.' });

  return out.length ? out : [{ icon: '☀️', text: 'Quiet conditions — enjoy.' }];
}

// Pressure trend over the last 3 hours, derived from hourly.pressure_msl.
export function getPressureTrend() {
  const f = STATE.forecast;
  if (!f || !f.hourly || !f.hourly.pressure_msl) return null;
  const idx = hourlyIndexForNow(f);
  const slice = f.hourly.pressure_msl.slice(Math.max(0, idx - 3), idx + 1).filter((v) => isFinite(v));
  if (slice.length < 2) return null;
  const delta = slice[slice.length - 1] - slice[0];
  // Convert hPa delta to inHg (×0.02953)
  const inHg = delta * 0.02953;
  const dir = inHg > 0.02 ? 'rising' : inHg < -0.02 ? 'falling' : 'steady';
  const arrow = dir === 'rising' ? '↑' : dir === 'falling' ? '↓' : '→';
  return { dir, inHg, label: `${arrow} ${Math.abs(inHg).toFixed(2)} in / 3h` };
}

export function getNarrative(state) {
  if (state) {
    switch (state) {
      case 'rain':  return 'Showers move in by lunchtime, easing toward dusk. Bring an umbrella, leave the sunglasses.';
      case 'storm': return 'Convection builds through the afternoon. Strong storms possible after 4 PM.';
      case 'snow':  return 'Steady snow through the morning, accumulating an inch or two before tapering off.';
      case 'fog':   return 'Dense fog hangs in the river valleys. Plan extra time for the commute.';
      case 'clear': return 'A near-perfect day — mild sun and a soft breeze.';
      default:      return 'A quiet, overcast stretch. Comfortable temperatures, no precipitation in the cards.';
    }
  }
  // Auto-generate based on real conditions
  const cur = getCurrentRealOrNull();
  if (!cur) return 'Pulling the latest from Open-Meteo…';
  return getNarrative(stateFromIcon(cur.icon));
}

// Phase-1 dev-state URL param (preserved through Phase 2 for visual checks).
export function devStateOverride(fallback) {
  const p = new URLSearchParams(globalThis.location?.search || '');
  const s = p.get('_dev_state');
  return ['clear','cloudy','rain','storm','snow','fog'].includes(s) ? s : (fallback || null);
}

// ───────────────────── Bootstrap ───────────────────────────────────────────
// Deliberately NOT run at module scope. The bundle executes on every viewport
// (the ≤500px guard in bootstrap.jsx only gates the UI mount), so a module-
// scope kickoff here fires a full forecast/alerts/archive/auth burst alongside
// the desktop engine's own load on desktop-width screens — the page pays for
// two engines when only one renders. bootstrap.jsx calls initDataBridge()
// right before mounting the mobile UI, so these fetches run only when this
// engine actually owns the screen.
let initialized = false;
export function initDataBridge() {
  if (initialized) return;
  initialized = true;
  const stored = readLastLocation();
  const initial = stored || FALLBACK_LOCATION;
  STATE.location = initial;
  STATE.unit = readUnit();
  loadAll(initial);
  loadAuth().then(() => { if (STATE.auth.authenticated) hydrateFromAccount(); });
  if (!stored) maybeAutoLocateFirstLoad();

  document.addEventListener?.('tenant-auth:claim-complete', () => {
    void hydrateFromAccount();
  });
  document.addEventListener?.('visibilitychange', () => {
    if (document.visibilityState === 'visible') hydrateAccountOnResume();
  });

  if (typeof globalThis !== 'undefined' && globalThis.addEventListener) {
    addEventListener('focus', hydrateAccountOnResume);
    // Desktop chrome (script.js / wx-app.js) writes the same localStorage keys
    // when the user picks a location there. 'storage' events fire across tabs;
    // for same-tab updates we'd need to poll or patch. Phase-2 stays passive.
    addEventListener('storage', (e) => {
      if (e.key === KEYS.lastLocation && e.newValue) {
        try {
          const loc = JSON.parse(e.newValue);
          if (loc && isFinite(loc.lat) && isFinite(loc.lon)) loadAll(loc);
        } catch (_) {}
      } else if (e.key === KEYS.unit) {
        patch({ unit: readUnit() });
      }
    });
  }
}
